import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CalendarEventVersionVisibilityTypesEnum} from '../../../models/api/calendarEventVersionModel';
import {StyledCalendarDropdownBoxCoordinator} from './calendarDropdownBoxBase';

/*
 * Props.
 */

interface CalendarVisibilityDropdownBoxProps {
  visibility: CalendarEventVersionVisibilityTypesEnum;
  onVisibilityChange: HandlerOf<CalendarEventVersionVisibilityTypesEnum>;
  shouldDisablePrivateVisibility: boolean;
  shouldHidePublicVisibility: boolean;
  className?: string;
}

/*
 * Styles.
 */

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledValueIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledContentWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 12px;
  align-items: center;
`;

/*
 * Component.
 */

export const CalendarVisibilityDropdownBox: FC<CalendarVisibilityDropdownBoxProps> = (props) => (
  <StyledCalendarDropdownBoxCoordinator
    value={renderValue(props)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
    boxClassName={props.className}
  />
);

function renderValue(props: CalendarVisibilityDropdownBoxProps) {
  return (
    <StyledValueDiv>
      {maybeRenderValueIcon(props.visibility)}
      {renderText(props.visibility)}
    </StyledValueDiv>
  );
}

function maybeRenderValueIcon(visibility: CalendarEventVersionVisibilityTypesEnum) {
  if (visibility === CalendarEventVersionVisibilityTypesEnum.DEFAULT) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => <StyledValueIcon name={getIconName(visibility)} color={theme.greys.shade70} />}
    </UserThemeConsumer>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, props: CalendarVisibilityDropdownBoxProps) {
  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        {_(CalendarEventVersionVisibilityTypesEnum)
          .values()
          .filter(
            (v) => v !== CalendarEventVersionVisibilityTypesEnum.PUBLIC || !props.shouldHidePublicVisibility,
          )
          .map((v) => renderItem(v, props))
          .value()}
      </DropdownList>
    </Dropdown>
  );
}

function renderItem(
  visibility: CalendarEventVersionVisibilityTypesEnum,
  props: CalendarVisibilityDropdownBoxProps,
) {
  const isChecked = visibility === props.visibility;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <DropdownItem
          key={visibility}
          isChecked={isChecked}
          isDisabled={
            props.shouldDisablePrivateVisibility &&
            visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE
          }
          onSelect={() => props.onVisibilityChange(visibility)}
        >
          <DropdownItemContent isChecked={isChecked}>
            <StyledContentWrapperDiv>
              <Icon name={getIconName(visibility)} color={theme.greys.shade70} />
              {renderText(visibility)}
            </StyledContentWrapperDiv>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </UserThemeConsumer>
  );
}

function getIconName(visibility: CalendarEventVersionVisibilityTypesEnum) {
  switch (visibility) {
    case CalendarEventVersionVisibilityTypesEnum.PRIVATE:
      return 'lockSmall';
    case CalendarEventVersionVisibilityTypesEnum.PUBLIC:
      return 'sharedFilledSmall';
    default:
      return 'calendarFilledSmall';
  }
}

function renderText(visibility: CalendarEventVersionVisibilityTypesEnum) {
  switch (visibility) {
    case CalendarEventVersionVisibilityTypesEnum.PRIVATE:
      return <Trans id="calendar-visibility-private">Private</Trans>;
    case CalendarEventVersionVisibilityTypesEnum.PUBLIC:
      return <Trans id="calendar-visibility-public">Public</Trans>;
    default:
      return <Trans id="calendar-visibility-default">Default visibility</Trans>;
  }
}
